import { type FC, useState } from 'react';
// Import UI Components
import { Icon, Popover } from '@geneui/components';
// Import Types
import type { DependencyPopoverPosition } from './FilterDependencyDots';
// Import Components
import DependencyTile from './DependencyTile';
// Import Styles
import styles from './FilterDependencyDots.module.scss';

export interface FilterInfoPopoverProps {
    /** Filter/KPI name shown as the popover title. */
    title: string;
    description: string;
    /** Hides the popover while the owner is being dragged. */
    isDragging?: boolean;
    className?: string;
    /** Side the hover popover opens on; use `left` for tiles near the right edge of the viewport. */
    popoverPosition?: DependencyPopoverPosition;
}

/**
 * Info tile (20x20, `bc-icon-info`) that opens a hover popover with the filter name and its description,
 * matching the parameter cards of the Custom Formula builder.
 */
const FilterInfoPopover: FC<FilterInfoPopoverProps> = ({
    title,
    description,
    isDragging,
    className,
    popoverPosition = 'right',
}) => {
    const [isHovered, setIsHovered] = useState(false);

    return (
        <Popover
            isOpen={isHovered && !isDragging}
            extendTargetWidth={false}
            scrollbarNeeded={false}
            position={popoverPosition}
            className={styles.hoverPopover}
            Content={
                <div className={styles.popoverContent}>
                    <span className={styles.popoverTitle}>{title}</span>
                    <span className={styles.popoverDescription}>{description}</span>
                </div>
            }
        >
            <DependencyTile
                className={className}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Icon className={styles.dependencyInfoIcon} type="bc-icon-info" />
            </DependencyTile>
        </Popover>
    );
};

export default FilterInfoPopover;
